import { getFolders, renameFolder, deleteFolder, moveFolder } from './folderHelpers';
import { getDocs, deleteDoc, moveDoc, renameDoc } from './docHelpers';
import { apiAddDoc, apiRemoveDoc, apiGetProjectsForDoc, apiGetDocs } from '../api/docs';
import { notifySuccess, notifyError } from './notificationHelpers';
import { apiMoveItemsToProject } from '../api/move';
import { apiRenameProject, apiGetProjects, apiCreateProject, apiDeleteProject } from '../api/projects';
import { debug } from './debug';
import { itemMapKey, folderMapKey, findItem } from './mapKey';
import { createItemComparator } from './sortHelpers';

const escapeRegex = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const nextIndexedName = (baseName, existingNames = []) => {
  const base = String(baseName || '').trim() || 'Untitled';
  const taken = new Set();
  const re = new RegExp(`^${escapeRegex(base)}\\s+(\\d+)$`);

  (Array.isArray(existingNames) ? existingNames : []).forEach((name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;

    if (trimmed === base) {
      taken.add(1);
      return;
    }

    const match = trimmed.match(re);
    if (match) taken.add(Number(match[1]));
  });

  let next = 1;
  while (taken.has(next)) next += 1;
  return next === 1 ? base : `${base} ${next}`;
};

// ----------------- Project Utilities -----------------

export const getDefaultProject = async (userId) => {
  try {
    const projects = await apiGetProjects(userId);
    return projects.length > 0 ? projects[0] : null;
  } catch (err) {
    console.error('[projectHelpers] getDefaultProject error:', err);
    return null;
  }
};

export const renameProject = async (projectId, newName) => {
  if (!projectId || !newName) return null;
  const trimmed = newName.trim() || 'Untitled Project';
  try {
    return await apiRenameProject(projectId, trimmed);
  } catch (err) {
    console.error('[projectHelpers] renameProject error:', err);
    return null;
  }
};

export const createProject = async (userId, preferredName = 'My Project') => {
  if (!userId) return null;
  try {
    const projects = await apiGetProjects(userId);
    const names = (projects || []).map(p => p.name || '');
    const unique = nextIndexedName(preferredName, names);

    const created = await apiCreateProject(userId, unique);
    return created;
  } catch (err) {
    console.error('[projectHelpers] createProject error', err);
    return null;
  }
};

export const deleteProject = async (projectId, userId) => {
  if (!projectId) return false;
  try {
    // Get all docs in this project
    const docs = await apiGetDocs(projectId);
    
    // Delete each doc (which will delete associated chunks via deleteDoc helper)
    if (docs && docs.length > 0) {
      for (const doc of docs) {
        try {
          // deleteDoc handles chunk cleanup and checks if doc is used in other projects
          await deleteDoc(doc.doc_id || doc.id, projectId, new Map(), () => {}, [], () => {});
        } catch (err) {
          console.warn(`[deleteProject] Failed to delete doc ${doc.id}:`, err);
        }
      }
    }
    
    // Delete the project itself
    const result = await apiDeleteProject(projectId);
    return result;
  } catch (err) {
    console.error('[projectHelpers] deleteProject error', err);
    return false;
  }
};

// ----------------- Tree Utilities -----------------

export const cloneEntryForMap = (raw) => ({
  id: raw.id,
  name: raw.name?.trim() || raw.title?.trim() || 'Untitled',
  content: raw.content ?? null,
  parent_id: raw.parent_id ?? raw.parent_folder_id ?? null,
  type: raw.type
});

export const buildFlatItems = ({ groups = [], filterItem = null, sortItems = null } = {}) => {
  const flat = [];

  (Array.isArray(groups) ? groups : []).forEach((group) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    const mapItem = typeof group?.mapItem === 'function' ? group.mapItem : (item) => item;
    items.forEach((item) => {
      flat.push(mapItem(item));
    });
  });

  let next = flat.filter(Boolean);
  if (typeof filterItem === 'function') next = next.filter(filterItem);
  if (typeof sortItems === 'function') next.sort(sortItems);
  return next;
};

export const buildItemsMap = (items = []) => {
  const itemsMap = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item?.type || item.id == null) return;
    itemsMap.set(itemMapKey(item), {
      ...item,
      children: []
    });
  });

  // Defensive normalization for corrupt rows where a folder points to itself.
  itemsMap.forEach((item) => {
    if (item.parent_id === item.id) item.parent_id = null;
  });

  itemsMap.forEach((item) => {
    if (!item.parent_id) return;
    const parentKey = folderMapKey(item.parent_id);
    const parent = itemsMap.get(parentKey);
    if (parent) parent.children.push(item);
  });

  const rootItems = [...itemsMap.values()].filter((item) => !item.parent_id);
  return { itemsMap, rootItems };
};

export const isDescendant = (itemsMap, ancestorId, possibleDescendantId) => {
  const cur = findItem(itemsMap, possibleDescendantId);
  if (!cur) return false;

  let parent = cur.parent_id;
  while (parent != null) {
    if (parent === ancestorId) return true;
    const p = findItem(itemsMap, parent, 'folder');
    if (!p) break;
    parent = p.parent_id;
  }
  return false;
};

// ----------------- Item Operations -----------------

export const getItems = async (projectId) => {
  if (!projectId) return { itemsMap: new Map(), rootItems: [] };
  try {
    const foldersRes = await getFolders(projectId);
    const docsRes = await getDocs(projectId);

    const allItems = buildFlatItems({
      groups: [
        {
          items: foldersRes,
          mapItem: (folder) => ({ ...folder, type: 'folder' })
        },
        {
          items: docsRes,
          mapItem: (doc) => ({ ...doc, type: 'doc' })
        }
      ],
      // Chats are surfaced in ChatsPanel and should not appear in the ProjectPanel tree.
      filterItem: (item) => item?.type !== 'doc' || String(item?.docType || '').toLowerCase() !== 'chat'
    });

    return buildItemsMap(allItems);
  } catch (err) {
    console.error('[projectHelpers] getItems error', err);
    return { itemsMap: new Map(), rootItems: [] };
  }
};

// ----------------- Item Operations -----------------

export const deleteItem = async (item, projectId, userId, itemsMap, setItemsMap, tabs, setTabs, setActiveTabId) => {
  debug('deleteItem', { type: 'audit', level: 1 }, item, itemsMap, setItemsMap);

  if (!(itemsMap instanceof Map)) {
    console.error('[deleteItem] itemsMap is not a Map', itemsMap);
    return null;
  }
  if (!item || !itemsMap || !setItemsMap) return null;

  const idsToDelete = new Set();
  const collectDescendants = (id) => {
    idsToDelete.add(id);
    itemsMap.forEach(child => {
      if (child.parent_id === id) collectDescendants(child.id);
    });
  };
  if (item.type === 'folder') {
    collectDescendants(item.id);
  } else {
    idsToDelete.add(item.id);
  }

  let deleted = false;
  if (item.type === 'folder') {
    deleted = !!(await deleteFolder(item.id, itemsMap, setItemsMap, projectId, userId));
  } else {
    deleted = !!(await deleteDoc(item.id, projectId, itemsMap, setItemsMap, tabs, setTabs, userId));
  }

  if (!deleted) return false;

  if (tabs && setTabs) {
    const remainingTabs = tabs.filter(t => !idsToDelete.has(t.docId));
    setTabs(remainingTabs);
    if (setActiveTabId) setActiveTabId(remainingTabs[0]?.id ?? null);
  }

  return true;
};

export const renameItem = async (itemId, type, newName, itemsMap, setItemsMap, tabs = null, setTabs = null, projectId = null, userId = null) => {
  debug('renameItem', { type: 'audit', level: 1 }, itemId, type, newName, itemsMap, setItemsMap);
  if (!itemId || !itemsMap || !setItemsMap || !newName) return null;

  const trimmed = newName.trim() || (type === 'folder' ? 'Untitled Folder' : 'Untitled Doc');
  const item = findItem(itemsMap, itemId, type);
  if (!item) return null;

  let result = null;
  if (type === 'folder') {
    result = await renameFolder(itemId, trimmed, itemsMap, setItemsMap, projectId, userId);
  } else {
    result = await renameDoc(itemId, trimmed, itemsMap, setItemsMap, projectId, userId);
  }

  if (tabs && setTabs && type === 'doc') {
    setTabs(prev => prev.map(t => t.docId === itemId ? { ...t, label: trimmed } : t));
  }

  return result;
};

export const moveItem = async (itemId, newParentId, type, itemsMap, setItemsMap, projectId = null, userId = null) => {
  debug('moveItem', { type: 'log' }, `${type} ${itemId} → ${newParentId || 'root'}`);
  if (!itemId || !itemsMap) return null;

  const item = findItem(itemsMap, itemId, type);
  if (!item) return null;

  let result = null;
  if (type === 'folder') {
    result = await moveFolder(itemId, newParentId, itemsMap, setItemsMap, projectId, userId);
  } else {
    result = await moveDoc(itemId, newParentId, itemsMap, setItemsMap, projectId, userId);
  }

  return result;
};

/**
 * Move docs/folders between projects by reassigning `project_*` references.
 *
 * Default behavior is RPC-first (`move_items_to_project`) for atomic,
 * non-destructive moves that do not touch doc/chunk/R2 content.
 *
 * Legacy client fallback can be enabled explicitly with
 * `allowClientFallback: true`.
 */
export const moveItemsToProject = async ({
  items = [],
  srcProjectId = null,
  destProjectId = null,
  destParentId = null,
  preferRpc = true,
  allowClientFallback = false,
  itemsMap = null,
  setItemsMap = null,
  tabs = null,
  setTabs = null,
  setActiveTabId = null,
  refresh = null
} = {}) => {
  if (!Array.isArray(items) || items.length === 0) return { moved: [], skipped: [] };
  if (!srcProjectId || !destProjectId) throw new Error('Missing project ids');
  if (String(srcProjectId) === String(destProjectId)) throw new Error('Destination must be a different project');

  debug('moveItemsToProject.start', { type: 'audit', level: 1 }, {
    srcProjectId,
    destProjectId,
    destParentId,
    itemCount: items.length,
    itemTypes: items.map(i => i?.type),
    preferRpc,
    allowClientFallback
  });

  // Prefer server-side RPC for atomic, permission-checked moves
  if (preferRpc) {
    try {
      const rpcItems = items.map(i => ({ type: i.type, id: Number(i.id) }));
      const res = await apiMoveItemsToProject(srcProjectId, destProjectId, rpcItems, destParentId, 'skip');
      debug('moveItemsToProject.rpc.response', { type: 'audit', level: 2 }, res);

      // rpc returns moved counts and conflicts
      const movedCount = Number(res?.moved_docs || 0);
      const movedFolders = Number(res?.moved_folders || 0);

      // Refresh UI trees
      try {
        if (typeof refresh === 'function') {
          await refresh(srcProjectId);
          await refresh(destProjectId);
        }
      } catch (e) { /* ignore */ }

      // Close tabs for moved docs
      try {
        if (Array.isArray(tabs) && typeof setTabs === 'function') {
          const movedDocIds = new Set((items || []).filter(it => it?.type === 'doc').map(it => Number(it.id)));
          const remaining = (tabs || []).filter(t => !movedDocIds.has(Number(t.docId)));
          setTabs(remaining);
          if (typeof setActiveTabId === 'function') setActiveTabId(remaining[0]?.id ?? null);
        }
      } catch (e) { debug('moveItemsToProject: tabs update failed', { type: 'warn' }, e); }

      if (movedCount > 0 || movedFolders > 0) {
        notifySuccess(`${movedCount + movedFolders} item${movedCount + movedFolders === 1 ? '' : 's'} moved to project`);
      }
      if (Array.isArray(res?.conflicts) && res.conflicts.length > 0) {
        notifyError(`${res.conflicts.length} conflicting docs skipped`, { verboseMessage: JSON.stringify(res.conflicts) });
      }

      debug('moveItemsToProject.rpc.success', { type: 'audit', level: 1 }, {
        movedCount,
        movedFolders,
        conflicts: Array.isArray(res?.conflicts) ? res.conflicts.length : 0
      });

      return { moved: rpcItems, skipped: res?.conflicts || [] };
    } catch (rpcErr) {
      debug('moveItemsToProject.rpc.error', { type: 'error' }, rpcErr);
      if (!allowClientFallback) {
        throw rpcErr;
      }
      debug('moveItemsToProject RPC failed, falling back to client flow', { type: 'warn' }, rpcErr);
      // Fallback to client-only doc moves for docs (no folder cloning)
    }
  }

  // Client fallback (docs only)
  const moved = [];
  const skipped = [];

  for (const item of items) {
    if (!item) continue;
    if (item.type === 'folder') {
      skipped.push({ item, reason: 'folder_move_requires_server_rpc' });
      continue;
    }

    const docId = item.id;
    try {
      const refs = await apiGetProjectsForDoc(docId);
      const already = Array.isArray(refs) && refs.some(r => String(r.project_id) === String(destProjectId));
      if (already) {
        skipped.push({ item, reason: 'already_in_destination' });
        continue;
      }

      await apiAddDoc(destProjectId, docId, destParentId ?? null);
      try {
        await apiRemoveDoc(srcProjectId, docId);
      } catch (removeErr) {
        try { await apiRemoveDoc(destProjectId, docId); } catch (_) {}
        skipped.push({ item, reason: 'remove_failed', error: removeErr });
        continue;
      }

      moved.push(item);
    } catch (err) {
      skipped.push({ item, reason: 'error', error: err });
    }
  }

  // Update local itemsMap
  try {
    if (itemsMap instanceof Map && typeof setItemsMap === 'function') {
      const next = new Map(itemsMap);
      moved.forEach(it => { if (it && it.id) next.delete(`doc:${it.id}`); });
      setItemsMap(next);
    }
  } catch (e) { debug('moveItemsToProject: itemsMap update failed', { type: 'warn' }, e); }

  // Close tabs
  try {
    if (Array.isArray(tabs) && typeof setTabs === 'function') {
      const movedIds = new Set(moved.map(m => Number(m?.id)));
      const remaining = (tabs || []).filter(t => !movedIds.has(Number(t.docId)));
      setTabs(remaining);
      if (typeof setActiveTabId === 'function') setActiveTabId(remaining[0]?.id ?? null);
    }
  } catch (e) { debug('moveItemsToProject: tabs update failed', { type: 'warn' }, e); }

  try {
    if (typeof refresh === 'function') {
      await refresh(srcProjectId);
      await refresh(destProjectId);
    }
  } catch (_) {}

  if (moved.length > 0) notifySuccess(`${moved.length} item${moved.length === 1 ? '' : 's'} moved to project`);
  if (skipped.length > 0) notifyError(`${skipped.length} item(s) were not moved`, { verboseMessage: JSON.stringify(skipped.slice(0,5)) });

  debug('moveItemsToProject.fallback.complete', { type: 'audit', level: 1 }, {
    movedCount: moved.length,
    skippedCount: skipped.length
  });

  return { moved, skipped };
};

// ----------------- Tree Builder -----------------

export const buildTree = (itemsInput, sortOptions = {}) => {
  const comparator = createItemComparator(
    sortOptions?.sortMode || 'alphabetical',
    {
      dateKeys: sortOptions?.dateKeys,
      manualKeys: sortOptions?.manualKeys
    }
  );
  const itemsFlat = Array.isArray(itemsInput) ? itemsInput : [...itemsInput.values()];
  const map = new Map();
  const tree = [];

  itemsFlat.forEach(item => { 
    if (!item || !item.id) return;
    const key = item.type ? itemMapKey(item) : String(item.id);
    map.set(key, { ...item, children: [] });   
  });
  

  map.forEach(item => { 
    const parentId = item.parent_id;
    const parentKey = parentId != null ? folderMapKey(parentId) : null;
    if (parentKey && map.has(parentKey)) { 
      map.get(parentKey).children.push(item);
    } else { 
      tree.push(item);
    }
  });

  // Sort tree and children
  //console.log("Tree before sort:", tree.map(i => i.id));
  tree.sort(comparator);
  map.forEach(item => {
    item.children.sort(comparator);
  });

  //console.log("Tree built:", tree);
  return tree;
};

